import { Controller } from "@hotwired/stimulus";
import { createRoot } from "react-dom/client";
import { Wrapper as QueryClientProvider } from "../helpers/queryClient";
import PermissionPicker from "../components/shared/PermissionPicker";

// Connects to data-controller="permission-picker"
// Intended to be used on a text input containing JSON.
//
// groupId + groupName flow in via data-permission-picker-group-{id,name}-value (set by
// HAML on Group context). They're forwarded as PermissionPicker's `fallbackGroup` prop
// so a network admin who isn't a member of the group still sees group_admin / group
// options — otherwise Preload.my_groups misses and only systemRoles get rendered.
//
// Optional opt-in client-side "must not be empty" validation:
//   data-permission-picker-required-value="true"
//   data-permission-picker-required-message-value="..."
export default class extends Controller {
  static values = {
    groupSlug: String,
    groupId: String,
    groupName: String,
    includeAuthorRole: Boolean,
    required: Boolean,
    requiredMessage: String,
  };

  connect() {
    this.originalType = this.element.type;
    this.element.type = "hidden";

    this.errorContainer = document.createElement("div");
    this.errorContainer.className =
      "text-danger mt-1 permission-picker-error hidden";
    this.element.insertAdjacentElement("afterend", this.errorContainer);

    const container = document.createElement("div");
    this.errorContainer.insertAdjacentElement("afterend", container);
    this.root = createRoot(container);
    const initialValue = JSON.parse(this.element.value || "[]");
    this.handleChange(initialValue);

    if (this.requiredValue) {
      this.form = this.element.closest("form");
      this.form?.addEventListener("submit", this.validateOnSubmit);
    }
  }

  handleChange = (newValue) => {
    this.element.value = JSON.stringify(newValue);
    if (newValue.length > 0) this.hideError();

    const query = {
      include_membership_roles: true,
      ...(this.groupSlugValue && {
        group_slug: this.groupSlugValue,
        section: "members",
        per_page: 25,
      }),
    };

    const fallbackGroup =
      this.groupIdValue && this.groupNameValue
        ? { id: this.groupIdValue, name: this.groupNameValue }
        : undefined;

    this.root.render(
      <QueryClientProvider>
        <PermissionPicker
          value={newValue}
          onChange={this.handleChange}
          query={query}
          includeAuthorRole={this.includeAuthorRoleValue}
          fallbackGroup={fallbackGroup}
        />
      </QueryClientProvider>,
    );
  };

  validateOnSubmit = (event) => {
    const value = JSON.parse(this.element.value || "[]");
    if (value.length === 0) {
      event.preventDefault();
      event.stopPropagation();
      this.showError();
      this.element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  showError() {
    this.errorContainer.textContent = this.requiredMessageValue;
    this.errorContainer.classList.remove("hidden");
  }

  hideError() {
    this.errorContainer.classList.add("hidden");
  }

  disconnect() {
    if (this.root) {
      this.root.unmount();
      this.element.type = this.originalType;
    }
    if (this.form) {
      this.form.removeEventListener("submit", this.validateOnSubmit);
    }
    this.errorContainer?.remove();
  }
}
